import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Mail, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FloatingLanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { trackEvent } from '../lib/analytics';
import api from '../lib/api';
import { destinoDespuesDeEntrar } from '../lib/rutaPendiente';
import { avisoDelFreno } from '../lib/avisoDelFreno';

export default function VerifyEmailPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!email) {
      navigate('/signup', { replace: true });
      return;
    }
    inputRef.current?.focus();
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // post-t7-2c (D5): `valor` es el codigo recien tecleado (el envio automatico del sexto digito); sin el, el del
  // estado (el boton y Enter). Antes el envio automatico leia el estado de la pintada anterior, con cinco digitos.
  const handleVerify = async (e, valor) => {
    e?.preventDefault();
    const elCodigo = typeof valor === 'string' ? valor : code;
    if (elCodigo.length !== 6) {
      toast.error(t('verify_email.toasts.enter_code'));
      return;
    }
    setLoading(true);
    try {
      await verifyEmail(email, elCodigo);
      toast.success(t('verify_email.toasts.verified'));
      trackEvent('email_verified', { email });
      navigate(destinoDespuesDeEntrar(), { replace: true });
    } catch (err) {
      // post-t7-2b remate (R2, 5.3): el 429 del freno, en el idioma del usuario.
      toast.error(avisoDelFreno(err, t) || err.response?.data?.detail || t('verify_email.toasts.invalid_code_fallback'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post('/auth/resend-verification', { email });
      // post-t7-2b remate (R2, 5.2): un reenvio que no salio (y sin codigo) no dice "reenviado".
      // Una respuesta sin `email_sent` (un servidor viejo) sigue como antes. El cooldown, igual.
      const datos = res?.data;
      if (datos && datos.email_sent === false && !datos.code) {
        toast.error(t('common:prod.codigo.nosalio'));
      } else {
        toast.success(t('verify_email.toasts.resent'));
      }
      setResendCooldown(60);
    } catch (err) {
      toast.error(avisoDelFreno(err, t) || err.response?.data?.detail || t('verify_email.toasts.resend_failed_fallback'));
    } finally {
      setResending(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    if (value.length === 6) {
      // Auto-submit when 6 digits entered
      setTimeout(() => handleVerify(undefined, value), 100);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0e0e0e]">
      <FloatingLanguageSelector />
      <div className="w-full max-w-md space-y-8">
        <button onClick={() => navigate('/signup')} className="flex items-center gap-2 text-[#a0a0a0] hover:text-white transition h-11 text-sm" data-testid="verify-back-btn">
          <ArrowLeft className="w-4 h-4" /> {t('verify_email.back_btn')}
        </button>

        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF6B00]/10 mb-4">
            <Mail className="w-8 h-8 text-[#FF6B00]" />
          </div>
          <h1 className="text-3xl font-bold text-white" data-testid="verify-title">{t('verify_email.title')}</h1>
          <p className="text-[#6a6a6a] mt-2">
            {t('verify_email.subtitle_prefix')} <span className="text-white font-medium">{email}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-5" data-testid="verify-form">
          <div className="space-y-2">
            <Input
              ref={inputRef}
              type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              placeholder="000000"
              value={code} onChange={handleCodeChange}
              className="text-center text-2xl tracking-widest font-mono bg-[#181818] border-[#2a2a2a] text-white py-6"
              data-testid="verify-code-input"
            />
          </div>

          <Button type="submit" disabled={loading || code.length !== 6}
            className="w-full bg-[#FF6B00] hover:bg-[#FF8c3e] text-white font-semibold"
            data-testid="verify-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('verify_email.submit_btn')}
          </Button>

          <div className="text-center text-sm text-[#6a6a6a]">
            <p>{t('verify_email.didnt_receive')}</p>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={handleResend}
              disabled={resending || resendCooldown > 0}
              className="text-[#FF6B00] hover:text-[#FF8c3e] mt-1"
              data-testid="verify-resend-btn">
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {resendCooldown > 0 ? `${t('verify_email.resend_btn')} (${resendCooldown}s)` : t('verify_email.resend_btn')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
